import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CityCombobox } from '@/components/geo/CityCombobox';
import { ROUTES } from '@/config/routes';
import { createOrgAdminRequestApi, createOrganizationApi, getOrganizationsApi } from '@/lib/api/organizationsApi.ts';
import { getMeApi } from '@/lib/auth/authApi';
import { useAuthStore } from '@/stores/auth-store';
import { SportType } from '@shared/enums/domain.enum';
import type { Commune, Organization } from '@shared/types/entities';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Building,
  CheckCircle,
  ChevronRight,
  CircleDot,
  Clock,
  Globe,
  Search,
  Tag,
  Trophy,
  UserPlus,
} from 'lucide-react';

const SPORT_TYPES: { value: SportType; label: string }[] = [
  { value: SportType.PETANQUE, label: 'Pétanque' },
  { value: SportType.LYONNAISE, label: 'Boule lyonnaise' },
  { value: SportType.PROVENCAL, label: 'Jeu provençal' },
  { value: SportType.TOUS, label: 'Tous les sports de boules' },
];

const ALIAS_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function slugify(str: string) {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// ─── Mode sélection ───────────────────────────────────────────────

function ChoiceStep({ onChoose }: { onChoose: (m: 'create' | 'join') => void }) {
  return (
    <div className='w-full max-w-lg bg-white rounded-3xl p-8 shadow-xl'>
      <h1 className='mb-2 text-xl font-extrabold text-slate-900'>Comment souhaitez-vous commencer&nbsp;?</h1>
      <p className='mb-6 text-sm text-slate-500'>Créez votre organisation ou rejoignez-en une existante.</p>

      <div className='space-y-4'>
        {/* Créer */}
        <button
          onClick={() => onChoose('create')}
          className='group w-full flex items-center gap-4 rounded-2xl border-2 border-orange-100 bg-orange-50 p-5 text-left transition hover:border-orange-300 hover:bg-orange-100'
        >
          <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-orange-600 transition group-hover:bg-orange-200'>
            <Trophy className='h-6 w-6' />
          </div>
          <div className='flex-1 min-w-0'>
            <p className='font-bold text-slate-900'>Créer mon organisation</p>
            <p className='text-sm text-slate-500'>Mon organisation n'existe pas encore sur la plateforme</p>
          </div>
          <ChevronRight className='h-5 w-5 shrink-0 text-slate-300 transition group-hover:text-orange-500' />
        </button>

        {/* Rejoindre */}
        <button
          onClick={() => onChoose('join')}
          className='group w-full flex items-center gap-4 rounded-2xl border-2 border-blue-100 bg-blue-50 p-5 text-left transition hover:border-blue-300 hover:bg-blue-100'
        >
          <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-600 transition group-hover:bg-blue-200'>
            <UserPlus className='h-6 w-6' />
          </div>
          <div className='flex-1 min-w-0'>
            <p className='font-bold text-slate-900'>Rejoindre une organisation existante</p>
            <p className='text-sm text-slate-500'>Mon organisation est déjà sur BoulesConnect</p>
          </div>
          <ChevronRight className='h-5 w-5 shrink-0 text-slate-300 transition group-hover:text-blue-500' />
        </button>
      </div>
    </div>
  );
}

// ─── Mode rejoindre ───────────────────────────────────────────────

function JoinStep({ onBack }: { onBack: () => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState<Organization | null>(null);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 350);
    return () => clearTimeout(t);
  }, [query]);

  const { data: orgsData, isLoading: isSearching } = useQuery({
    queryKey: ['onboarding-org-search', debounced],
    queryFn: () => getOrganizationsApi({ search: debounced, limit: 8 }),
    enabled: debounced.length >= 2,
  });

  const suggestions = orgsData?.data ?? [];

  function handleSelect(org: Organization) {
    setSelected(org);
    setQuery(org.name);
    setShowDropdown(false);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setError(null);
    setIsSubmitting(true);
    try {
      await createOrgAdminRequestApi(selected.id, { message: message.trim() || undefined });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'envoi de la demande");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted && selected) {
    return (
      <div className='w-full max-w-lg bg-white rounded-3xl p-8 shadow-xl'>
        <div className='mb-5 flex items-center gap-3'>
          <CheckCircle className='h-10 w-10 shrink-0 text-emerald-500' />
          <div>
            <h2 className='text-xl font-extrabold text-slate-900'>Demande envoyée&nbsp;!</h2>
            <p className='text-sm text-slate-500'>
              Transmise aux responsables de <strong>{selected.name}</strong>.
            </p>
          </div>
        </div>

        <div className='mb-6 rounded-2xl bg-amber-50 border border-amber-100 p-4'>
          <div className='flex items-start gap-3'>
            <Clock className='mt-0.5 h-5 w-5 shrink-0 text-amber-500' />
            <div>
              <p className='text-sm font-bold text-amber-800'>En attente de validation</p>
              <p className='mt-1 text-sm text-amber-700'>
                Un responsable de l&apos;organisation doit accepter votre demande avant que vous puissiez accéder au
                tableau de bord organisateur. Vous recevrez un e-mail dès qu&apos;elle sera traitée.
              </p>
            </div>
          </div>
        </div>

        <p className='mb-3 text-xs text-slate-400 text-center'>
          En attendant, vous pouvez consulter votre espace joueur.
        </p>
        <button
          onClick={() => navigate(ROUTES.PLAYER_DASHBOARD)}
          className='w-full inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-100'
        >
          Aller à mon espace joueur <ChevronRight className='h-4 w-4' />
        </button>
      </div>
    );
  }

  return (
    <div className='w-full max-w-lg bg-white rounded-3xl p-8 shadow-xl'>
      <button
        onClick={onBack}
        className='mb-4 flex items-center gap-1.5 text-sm text-slate-400 transition hover:text-slate-700'
      >
        <ArrowLeft className='h-4 w-4' /> Retour
      </button>

      <div className='mb-6 flex items-center gap-3'>
        <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-100'>
          <UserPlus className='h-6 w-6 text-blue-600' />
        </div>
        <div>
          <h1 className='text-xl font-extrabold text-slate-900'>Rejoindre une organisation</h1>
          <p className='text-sm text-slate-500'>Recherchez votre organisation et envoyez une demande.</p>
        </div>
      </div>

      {error && (
        <div className='mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700'>{error}</div>
      )}

      <form onSubmit={handleSubmit} className='space-y-4'>
        {/* Recherche */}
        <div className='relative'>
          <label className='mb-1.5 block text-sm font-semibold text-slate-700'>
            Nom de l&apos;organisation <span className='text-red-500'>*</span>
          </label>
          <div className='relative'>
            <Search className='absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400' />
            <input
              ref={inputRef}
              type='text'
              value={query}
              onChange={e => {
                setQuery(e.target.value);
                setSelected(null);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              placeholder='Rechercher une organisation…'
              autoComplete='off'
              className='w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors'
            />
          </div>

          {/* Dropdown */}
          {showDropdown && debounced.length >= 2 && (
            <div className='absolute z-10 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-lg'>
              {isSearching ? (
                <p className='px-4 py-3 text-sm text-slate-400'>Recherche…</p>
              ) : suggestions.length === 0 ? (
                <p className='px-4 py-3 text-sm text-slate-400'>Aucune organisation trouvée.</p>
              ) : (
                <ul>
                  {suggestions.map(org => (
                    <li key={org.id}>
                      <button
                        type='button'
                        onClick={() => handleSelect(org)}
                        className='w-full flex items-center gap-3 px-4 py-3 text-left transition hover:bg-slate-50 first:rounded-t-xl last:rounded-b-xl'
                      >
                        <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-xs font-bold text-indigo-600'>
                          {org.name.slice(0, 2).toUpperCase()}
                        </div>
                        <div className='min-w-0'>
                          <p className='truncate text-sm font-semibold text-slate-900'>{org.name}</p>
                          {org.city && <p className='text-xs text-slate-400'>{org.city}</p>}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Message optionnel — visible seulement quand une org est sélectionnée */}
        {selected && (
          <>
            <div className='rounded-xl bg-blue-50 border border-blue-100 px-4 py-3 text-sm text-blue-800'>
              Organisation sélectionnée&nbsp;: <strong>{selected.name}</strong>
              {selected.city ? ` — ${selected.city}` : ''}
            </div>
            <div>
              <label className='mb-1.5 block text-sm font-semibold text-slate-700'>Message (optionnel)</label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder='Présentez-vous brièvement…'
                maxLength={500}
                rows={3}
                className='w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors'
              />
            </div>
            <button
              type='submit'
              disabled={isSubmitting}
              className='w-full flex items-center justify-center gap-2 bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors'
            >
              {isSubmitting ? 'Envoi…' : 'Envoyer ma demande'}
            </button>
          </>
        )}
      </form>
    </div>
  );
}

// ─── Mode création ─────────────────────────────────────────────────

function CreateStep({ onBack }: { onBack: () => void }) {
  const navigate = useNavigate();
  const { login, accessToken } = useAuthStore();

  const [name, setName] = useState('');
  const [alias, setAlias] = useState('');
  const [sportType, setSportType] = useState<SportType>(SportType.PETANQUE);
  const [city, setCity] = useState('');
  const [department, setDepartment] = useState('');
  const [region, setRegion] = useState('');
  const [website, setWebsite] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleNameBlur = () => {
    if (!alias && name) setAlias(slugify(name));
  };

  const handleCitySelect = (commune: Commune) => {
    setCity(commune.name);
    setDepartment(commune.departmentName ?? commune.department);
    setRegion(commune.regionName ?? commune.region ?? '');
  };

  const handleCityChange = (value: string) => {
    setCity(value);
    if (!value) {
      setDepartment('');
      setRegion('');
    }
  };

  const isAliasValid = alias.length === 0 || ALIAS_REGEX.test(alias);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isAliasValid) {
      setError("L'alias doit contenir uniquement des lettres minuscules, chiffres et tirets.");
      return;
    }

    setIsSubmitting(true);

    try {
      await createOrganizationApi({
        name: name.trim(),
        alias: alias.trim(),
        sportType,
        city: city || undefined,
        department: department || undefined,
        region: region || undefined,
        website: website.trim() || undefined,
      });

      const updatedUser = await getMeApi();
      login(updatedUser, accessToken!);

      navigate(ROUTES.ORGANIZER_DASHBOARD, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de la création de l'organisation");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='w-full max-w-lg bg-white rounded-3xl p-8 shadow-xl'>
      <button
        onClick={onBack}
        className='mb-4 flex items-center gap-1.5 text-sm text-slate-400 transition hover:text-slate-700'
      >
        <ArrowLeft className='h-4 w-4' /> Retour
      </button>

      <div className='mb-6 flex items-center gap-3'>
        <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-100'>
          <Trophy className='h-6 w-6 text-orange-600' />
        </div>
        <div>
          <h1 className='text-xl font-extrabold text-slate-900'>Créer mon organisation</h1>
          <p className='text-sm text-slate-500'>Vous pourrez organiser des concours et gérer des clubs affiliés.</p>
        </div>
      </div>

      {error && (
        <div className='mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700'>{error}</div>
      )}

      <form onSubmit={handleSubmit} className='space-y-4'>
        <div>
          <label className='block text-sm font-semibold text-slate-700 mb-1.5'>
            Nom de l&apos;organisation <span className='text-red-500'>*</span>
          </label>
          <div className='relative'>
            <Building className='absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
            <input
              type='text'
              required
              maxLength={100}
              value={name}
              onChange={e => setName(e.target.value)}
              onBlur={handleNameBlur}
              placeholder='Comité Départemental 13'
              className='w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors'
            />
          </div>
        </div>

        <div>
          <label className='block text-sm font-semibold text-slate-700 mb-1.5'>
            Identifiant unique (alias) <span className='text-red-500'>*</span>
          </label>
          <div className='relative'>
            <Tag className='absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
            <input
              type='text'
              required
              maxLength={100}
              pattern='[a-z0-9]+(?:-[a-z0-9]+)*'
              title='Lettres minuscules, chiffres et tirets uniquement'
              value={alias}
              onChange={e => setAlias(e.target.value)}
              placeholder='comite-departemental-13'
              className={`w-full pl-11 pr-4 py-3 bg-slate-50 border rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors font-mono text-sm ${
                alias && !isAliasValid ? 'border-red-300' : 'border-slate-200'
              }`}
            />
          </div>
          {alias && !isAliasValid && (
            <p className='mt-1 text-xs text-red-500'>Lettres minuscules, chiffres et tirets uniquement.</p>
          )}
        </div>

        <div>
          <label className='block text-sm font-semibold text-slate-700 mb-1.5'>
            Discipline <span className='text-red-500'>*</span>
          </label>
          <select
            required
            value={sportType}
            onChange={e => setSportType(e.target.value as SportType)}
            className='w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors'
          >
            {SPORT_TYPES.map(s => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className='block text-sm font-semibold text-slate-700 mb-1.5'>Ville</label>
          <CityCombobox
            value={city}
            onChange={handleCityChange}
            onSelect={handleCitySelect}
            placeholder='Rechercher une commune…'
            className='[&_input]:py-3'
          />
        </div>

        <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
          <div>
            <label className='block text-sm font-semibold text-slate-700 mb-1.5'>Département</label>
            <input
              type='text'
              readOnly
              maxLength={100}
              value={department}
              placeholder='Rempli automatiquement'
              className='w-full px-3 py-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-700 placeholder:text-slate-400 cursor-not-allowed text-sm'
            />
          </div>
          <div>
            <label className='block text-sm font-semibold text-slate-700 mb-1.5'>Région</label>
            <input
              type='text'
              readOnly
              maxLength={100}
              value={region}
              placeholder='Rempli automatiquement'
              className='w-full px-3 py-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-700 placeholder:text-slate-400 cursor-not-allowed text-sm'
            />
          </div>
        </div>

        <div>
          <label className='block text-sm font-semibold text-slate-700 mb-1.5'>Site web</label>
          <div className='relative'>
            <Globe className='absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
            <input
              type='url'
              maxLength={255}
              value={website}
              onChange={e => setWebsite(e.target.value)}
              placeholder='https://monorganisation.fr'
              className='w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm'
            />
          </div>
        </div>

        <button
          type='submit'
          disabled={isSubmitting || (alias.length > 0 && !isAliasValid)}
          className='w-full flex items-center justify-center gap-2 bg-orange-600 text-white font-bold py-3 rounded-xl hover:bg-orange-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors mt-2'
        >
          {isSubmitting ? (
            'Création en cours…'
          ) : (
            <>
              Créer mon organisation <ChevronRight className='w-4 h-4' />
            </>
          )}
        </button>
      </form>
    </div>
  );
}

// ─── Page principale ───────────────────────────────────────────────

export default function CreateOrgOnboarding() {
  const [mode, setMode] = useState<'choice' | 'create' | 'join'>('choice');

  return (
    <div className='min-h-screen bg-linear-to-br from-blue-600 to-indigo-700 flex flex-col items-center justify-center px-6 py-12'>
      {/* Brand */}
      <div className='flex items-center gap-3 mb-8'>
        <CircleDot className='w-10 h-10 text-white' />
        <span className='text-2xl font-extrabold text-white'>BoulesConnect</span>
      </div>

      {mode === 'choice' && <ChoiceStep onChoose={setMode} />}
      {mode === 'create' && <CreateStep onBack={() => setMode('choice')} />}
      {mode === 'join' && <JoinStep onBack={() => setMode('choice')} />}
    </div>
  );
}
